import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardBody, Col, Input, Row, Container, UncontrolledDropdown, DropdownToggle, DropdownMenu, DropdownItem, Button, UncontrolledTooltip } from 'reactstrap';
import DataTable from 'react-data-table-component';
import { MoreVertical, Eye, Edit2, Mail, Trash2, Search, Download, ChevronLeft, ChevronRight, List, Grid } from 'react-feather';
import debounce from 'lodash.debounce';
import { useNavigate } from 'react-router-dom';
import { connect } from 'react-redux';
import { toast } from 'react-toastify';
import {
  getBrandRequests as getBrandRequestsAction,
  deleteBrandRequest as deleteBrandRequestAction,
  approveBrandRequest as approveBrandRequestAction,
  adminUpdateBrandRequest as adminUpdateBrandRequestAction,
} from '../../ReduxToolkit/Reducers/brandRequests';
import SendInvitationModal from '../../Components/BrandRequests/SendInvitationModal';
import ConfirmationModal from '../../Components/ConfirmationModal';
import AlertJanModal from '../../Components/BrandRequests/AlertJanModal';
import axios from 'axios';
import { API_TOKEN, API_URL } from '../../Config/secrets';

// Add animation styles
const drawerAnimationStyles = `
@keyframes slideInRight {
  from {
    transform: translateX(100%);
  }
  to {
    transform: translateX(0);
  }
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}
`;

// Inject styles
if (typeof document !== 'undefined') {
  const styleSheet = document.createElement('style');
  styleSheet.textContent = drawerAnimationStyles;
  if (!document.head.querySelector('style[data-drawer-animations]')) {
    styleSheet.setAttribute('data-drawer-animations', 'true');
    document.head.appendChild(styleSheet);
  }
}

const BrandRequests = ({
  user,
  getBrandRequests,
  deleteBrandRequest,
  approveBrandRequest,
  adminUpdateBrandRequest,
  brandRequests,
  totalBrandRequests,
  isLoading,
}) => {
  const navigate = useNavigate();
  const hasApprovePermission = user?.permissions?.brand_request_approval?.view;
  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchText, setSearchText] = useState('');
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [statusFilter, setStatusFilter] = useState('All Status');
  const [matchTypeFilter, setMatchTypeFilter] = useState('All Match Types');
  const [dateFilter, setDateFilter] = useState('');
  const [confirmationModalOpen, setConfirmationModalOpen] = useState(false);
  const [singleDeleteBrandRequest, setSingleDeleteBrandRequest] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [invitationModalOpen, setInvitationModalOpen] = useState(false);
  const [selectedBrandRequest, setSelectedBrandRequest] = useState(null);
  const [tableData, setTableData] = useState([]);
  const [activeView, setActiveView] = useState('table');
  const [selectedBrandRequestIds, setSelectedBrandRequestIds] = useState([]);
  const [exportLoading, setExportLoading] = useState(false);
  const [singleExportLoadingId, setSingleExportLoadingId] = useState(null);
  const [selectAll, setSelectAll] = useState(false);
  const [approveLoadingId, setApproveLoadingId] = useState(null);
  const [alertJanModalOpen, setAlertJanModalOpen] = useState(false);
  const [alertJanBrandRequest, setAlertJanBrandRequest] = useState(null);
  const [historyDrawerOpen, setHistoryDrawerOpen] = useState(false);
  const [selectedHistoryRow, setSelectedHistoryRow] = useState(null);

  useEffect(() => {
    if (page) {
      _getTableData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, searchText, itemsPerPage, statusFilter, matchTypeFilter, dateFilter]);

  useEffect(() => {
    if (brandRequests[page]) {
      setTableData(brandRequests[page]);
    }
  }, [brandRequests, page]);

  useEffect(() => {
    if (selectAll) return;
    if (!tableData?.length) {
      setSelectedBrandRequestIds([]);
      return;
    }
    const currentIds = new Set(tableData.map((item) => item.id));
    setSelectedBrandRequestIds((prev) => prev.filter((id) => currentIds.has(id)));
  }, [tableData, selectAll]);

  const _getTableData = async (hideLoader = false) => {
    const status = statusFilter === 'All Status' ? undefined : statusFilter;
    const match_type = matchTypeFilter === 'All Match Types' ? undefined : matchTypeFilter;
    const created_at = dateFilter || undefined;
    await getBrandRequests(page, searchText, itemsPerPage, status, match_type, created_at, hideLoader);
  };

  const _searchRequests = (inputValue) => {
    const query = inputValue?.trim();
    setPage(1);
    setSearchText(query);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debounceFn = useCallback(debounce(_searchRequests, 1000), []);

  const handleEditBrandRequest = (row, editStep = 'brand_info') => {
    if (row?.status === 'Matches Processing') {
      return toast.warning('Cannot edit brand request while matches are processing.', { autoClose: 3000 });
    }
    navigate(`/brand-requests/create`, {
      state: {
        brandRequest: {
          id: row?.id,
          ...row?.data,
        },
        editStep: editStep,
      },
    });
  };

  const toggleInvitationModal = (row = null) => {
    setSelectedBrandRequest(row);
    setInvitationModalOpen(!invitationModalOpen);
  };

  const handleInvitationSent = async () => {
    await _getTableData(true);
  };

  const handleDelete = async () => {
    try {
      setDeleteLoading(true);
      const id = singleDeleteBrandRequest?.id;
      if (!id) return;

      const result = await deleteBrandRequest(id);
      if (result?.statusCode === 200 || result?.data?.statusCode === 200 || result?.statusCode === undefined) {
        await _getTableData(true);
      }
    } catch (e) {
      console.error('Delete error:', e);
    } finally {
      setDeleteLoading(false);
      setConfirmationModalOpen(false);
      setSingleDeleteBrandRequest(null);
    }
  };

  const handleStatusFilterChange = (status) => {
    setStatusFilter(status);
    setPage(1);
  };

  const handleMatchTypeFilterChange = (matchType) => {
    setMatchTypeFilter(matchType);
    setPage(1);
  };

  const handleDateFilterChange = (e) => {
    setDateFilter(e.target.value);
    setPage(1);
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    debounceFn(e.target.value);
  };

  const handleItemPerPageSelect = async (count) => {
    setPage(1);
    setItemsPerPage(count);
  };

  const handleOpenAlertJan = (row) => {
    setAlertJanBrandRequest(row);
    setAlertJanModalOpen(true);
  };

  const handleRowClick = (row) => {
    setSelectedHistoryRow(row);
    setHistoryDrawerOpen(true);
  };

  const closeHistoryDrawer = () => {
    setHistoryDrawerOpen(false);
    setSelectedHistoryRow(null);
  };

  const formatDetailDate = (dateString) => {
    if (!dateString) return '--';
    return new Date(dateString).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    }).replace(/ /g, "-");
  };

  const getElapsedMs = (from, to) => {
    if (!from || !to) return 0;
    return Math.max(0, new Date(to).getTime() - new Date(from).getTime());
  };

  const formatElapsedTime = (ms) => {
    if (!ms) return '0m';

    const totalMinutes = Math.floor(ms / 60000);
    const days = Math.floor(totalMinutes / (60 * 24));
    const hours = Math.floor((totalMinutes % (60 * 24)) / 60);
    const minutes = totalMinutes % 60;

    if (days > 0) {
      return hours > 0 ? `${days}d ${hours}h ${minutes}m` : `${days}d ${minutes}m`;
    }
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    if (minutes > 0) {
      return `${minutes}m`;
    }
    return `${Math.max(1, Math.floor(ms / 1000))}s`;
  };

  const getHistoryTimelineEvents = (row) => {
    const events = [
      {
        action: 'Brand Request created',
        action_by: row?.request_type === 'client' ? 'Client' : 'Admin',
        created_at: row?.created_at,
      },
    ];

    if (row?.history_logs?.logs?.length) {
      events.push(...row.history_logs.logs);
    }

    return events.sort(
      (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
    );
  };

  const getTotalAccumulativeTime = (row) => {
    const events = getHistoryTimelineEvents(row);
    if (events.length < 2) return 0;

    return events.slice(1).reduce((total, event, index) => {
      return total + getElapsedMs(events[index].created_at, event.created_at);
    }, 0);
  };

  const getAbsoluteUrl = (url) => {
    if (!url) return '';
    return url.startsWith('http') ? url : `https://${url}`;
  };

  const normalizeToArray = (value) => {
    if (!value) return [];
    return Array.isArray(value) ? value : [value];
  };

  const handleSelectBrandRequest = (id) => {
    setSelectAll(false);
    setSelectedBrandRequestIds((prev) => {
      if (prev.includes(id)) return prev.filter((selectedId) => selectedId !== id);
      return [...prev, id];
    });
  };

  const handleSelectAllDetailed = (checked) => {
    if (checked) {
      setSelectAll(true);
      setSelectedBrandRequestIds(tableData.map((item) => item.id));
    } else {
      setSelectAll(false);
      setSelectedBrandRequestIds([]);
    }
  };

  const _downloadCsvBlob = (response, fallbackName) => {
    const contentDisposition = response?.headers?.['content-disposition'] || '';
    const fileNameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
    const fileName = fileNameMatch?.[1] || fallbackName;
    const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const handleExportSelected = async () => {
    if (!selectAll && !selectedBrandRequestIds.length) {
      toast.warning('Please select at least one brand request to export.', { autoClose: 3000 });
      return;
    }

    try {
      setExportLoading(true);

      const payload = selectAll
        ? {
          skip: 1,
          take: 5000,
          ...(searchText && { query: searchText }),
          ...(statusFilter !== 'All Status' && { status: statusFilter }),
          ...(matchTypeFilter !== 'All Match Types' && { match_type: matchTypeFilter }),
          ...(dateFilter && { created_at: dateFilter }),
        }
        : { brand_ids: selectedBrandRequestIds };

      const response = await axios.post(
        `${API_URL}/brand-requests/export-brand-requests`,
        payload,
        {
          headers: { Authorization: API_TOKEN },
          responseType: 'blob',
        }
      );

      _downloadCsvBlob(response, `brand-requests.csv`);
      toast.success('CSV exported successfully.', { autoClose: 3000 });
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to export CSV.', { autoClose: 3000 });
    } finally {
      setExportLoading(false);
    }
  };

  const handleExportSingle = async (id) => {
    try {
      setSingleExportLoadingId(id);
      const response = await axios.post(
        `${API_URL}/brand-requests/export-brand-requests`,
        { brand_ids: [id] },
        {
          headers: { Authorization: API_TOKEN },
          responseType: 'blob',
        }
      );
      _downloadCsvBlob(response, `brandRequest-${id}.csv`);
      toast.success('CSV exported successfully.', { autoClose: 3000 });
    } catch (error) {
      toast.error('Failed to export CSV.', { autoClose: 3000 });
    } finally {
      setSingleExportLoadingId(null);
    }
  };

  const handleApproveBrandRequest = async (id) => {
    if (!hasApprovePermission) {
      return toast.warning('You do not have permission to approve brand requests.', { autoClose: 3000 });
    }
    try {
      setApproveLoadingId(id);
      const result = await approveBrandRequest(id, user?.name);
      if (result?.statusCode === 200 || result?.data?.statusCode === 200 || result?.statusCode === undefined) {
        await _getTableData(true);
      }
    } catch (error) {
      console.error('Approve error:', error);
    } finally {
      setApproveLoadingId(null);
    }
  };

  const allPageSelected = tableData.length > 0 && tableData.every((item) => selectedBrandRequestIds.includes(item.id));

  const totalPages = Math.ceil(totalBrandRequests / itemsPerPage) || 1;

  const renderActionButtons = (row) => {
    const inviteAlreadySent = row?.data?.invite_sent === true;
    const mailIconId = `mail-icon-${row.id}`;

    return (
      <div className="d-flex align-items-center gap-2">
        <button
          type="button"
          className="btn btn-link p-0"
          onClick={() => navigate(`/brand-requests/${row.id}`, { state: { brandRequest: row } })}
          title="View"
        >
          <Eye size={17} color="#111" />
        </button>
        <button
          type="button"
          className="btn btn-link p-0"
          onClick={() => handleEditBrandRequest(row)}
          title="Edit"
        >
          <Edit2 size={17} color="#111" />
        </button>
        <>
          <button
            id={mailIconId}
            type="button"
            className="btn btn-link p-0"
            onClick={() => !inviteAlreadySent && toggleInvitationModal(row)}
            title="Send Invite"
            disabled={inviteAlreadySent}
            style={{
              opacity: inviteAlreadySent ? 0.5 : 1,
              cursor: inviteAlreadySent ? 'not-allowed' : 'pointer',
            }}
          >
            <Mail size={17} color="#111" />
          </button>
          {inviteAlreadySent && (
            <UncontrolledTooltip placement="top" target={mailIconId}>
              Invite already sent
            </UncontrolledTooltip>
          )}
        </>
        <UncontrolledDropdown>
          <DropdownToggle tag="button" className="btn btn-link p-0">
            <MoreVertical size={17} color="#111" />
          </DropdownToggle>
          <DropdownMenu end>
            <DropdownItem
              onClick={() => {
                setSingleDeleteBrandRequest(row);
                setConfirmationModalOpen(true);
              }}
              className="text-danger"
            >
              <Trash2 size={14} className="me-1" /> Delete
            </DropdownItem>
            {row?.status === 'Request Added' && (
              <DropdownItem onClick={() => handleEditBrandRequest(row, 'find_matches')}>
                <Search size={14} className="me-1" /> Find Matches
              </DropdownItem>
            )}
          </DropdownMenu>
        </UncontrolledDropdown>
      </div>
    );
  };

  const columns = [
    {
      name: 'Brand Name',
      selector: (row) => row.brand_name || '--',
      sortable: false,
      center: false,
      minWidth: '150px',
    },
    {
      name: 'Profile',
      cell: (row) => {
        const id = row?.brand_id || row?.organization?.organization_brand[0]?.brand?.id;
        return id ? (
          <a
            href={`/profile/${id}/brand`}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: '#007bff', textDecoration: 'none' }}
          >
            Visit Profile
          </a>
        ) : (
          <span>--</span>
        );
      },
      sortable: false,
      center: false,
      minWidth: '200px',
    },
    {
      name: 'Website',
      cell: (row) => {
        const website = row.website;
        return website ? (
          <a
            href={website.startsWith('http') ? website : `https://${website}`}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: '#007bff', textDecoration: 'none' }}
          >
            Visit Website
          </a>
        ) : (
          <span>--</span>
        );
      },
      sortable: false,
      center: false,
      minWidth: '200px',
    },
    {
      name: 'Source',
      cell: (row) => row.request_type || 'admin',
      center: true,
      minWidth: '120px',
    },
    {
      name: 'Match Type',
      cell: (row) => row.match_type ? row.match_type : <span>--</span>,
      center: true,
      minWidth: '120px',
    },
    {
      name: 'Status',
      cell: (row) => (
        <span style={statusBadgeStyle}>
          {row.status}
        </span>
      ),
      center: true,
      minWidth: '140px',
    },
    {
      name: 'Created',
      cell: (row) => formatDetailDate(row.created_at),
      sortable: false,
      center: false,
      minWidth: '200px',
    },
    {
      name: 'Last Updated',
      cell: (row) => formatDetailDate(row.updated_at),
      sortable: false,
      center: false,
      minWidth: '200px',
    },
    // {
    //   name: 'Deadline',
    //   cell: (row) => row?.data?.brand_info?.deadline ? formatDetailDate(row?.data?.brand_info?.deadline) : '--',
    //   sortable: false,
    //   center: false,
    //   minWidth: '200px',
    // },
    {
      name: 'Approve',
      cell: (row) => {
        if (row.request_type === 'client' && row.approved === false) {
          const isApproving = approveLoadingId === row.id;
          return (
            <Button
              color="success"
              size="sm"
              onClick={() => handleApproveBrandRequest(row.id)}
              disabled={isApproving}
              className="d-flex align-items-center gap-1"
              style={{ fontSize: '12px', padding: '4px 12px' }}
            >
              {isApproving ? 'Approving...' : 'Approve'}
            </Button>
          );
        }
        return row.request_type === 'client' && row.approved === true ? (
          <span style={{ fontSize: '12px', color: '#28a745', fontWeight: '500' }}>✓ Approved</span>
        ) : '--';
      },
      center: true,
      minWidth: '120px',
    },
    {
      name: 'Send to Lead',
      cell: (row) => {
        const alertJanTooltipId = `alert-jan-tooltip-${row.id}`;
        return row.alert_jan_sent ? (
          <>
            <span id={alertJanTooltipId} style={{ fontSize: '12px', color: '#28a745', fontWeight: '500', cursor: 'default' }}>✓ Sent</span>
            <UncontrolledTooltip placement="top" target={alertJanTooltipId}>
              {row.alert_jan_sent}
            </UncontrolledTooltip>
          </>
        ) : (
          <Button
            color="success"
            size="sm"
            disabled={row.approved}
            className="d-flex align-items-center gap-1"
            style={{ fontSize: '12px', padding: '4px 12px' }}
            onClick={() => handleOpenAlertJan(row)}
          >
            Send
          </Button>
        );
      },
      center: true,
      minWidth: '120px',
    },
    {
      name: 'Approved By',
      cell: (row) => row.approved_by_admin || '--',
      center: true,
      minWidth: '120px',
    },
    {
      name: 'Actions',
      cell: (row) => renderActionButtons(row),
      center: true,
      minWidth: '160px',
    },
  ];

  const renderDetailedCard = (row) => {
    const detailData = row?.data || {};
    const brandInfo = detailData?.brand_info || {};
    const findMatches = detailData?.find_matches || {};
    const brandInSpace = detailData?.brand_in_space?.brands || [];
    const exclusions = detailData?.exclusions?.brands || [];
    const markets = Array.isArray(brandInfo?.markets_of_interest) ? brandInfo.markets_of_interest : [];
    const brandA = normalizeToArray(findMatches?.brand_a);
    const brandB = normalizeToArray(findMatches?.brand_b);
    const brandsInSpaceForMatch = normalizeToArray(findMatches?.brands_in_space);
    const isSelected = selectedBrandRequestIds.includes(row.id) || selectAll;
    const isSingleExporting = singleExportLoadingId === row.id;
    const profileBrandId = row?.organization?.organization_brand?.brand?.id;
    const displayWebsite = row.website || brandInfo?.website;

    return (
      <div
        key={row.id}
        style={{
          ...detailedCardStyle,
          borderColor: isSelected ? '#007bff' : '#e9ecef',
          boxShadow: isSelected
            ? '0 0 0 2px rgba(0,123,255,0.15), 0 2px 8px rgba(0,0,0,0.06)'
            : '0 2px 8px rgba(0,0,0,0.04)',
        }}
      >
        {/* Card Header */}
        <div style={cardHeaderStyle}>
          <div className="d-flex align-items-center gap-3" style={{ flex: 1, minWidth: 0 }}>
            <Input
              type="checkbox"
              checked={isSelected}
              onChange={() => handleSelectBrandRequest(row.id)}
              style={{ width: '18px', height: '18px', cursor: 'pointer', flexShrink: 0 }}
            />
            <div style={{ minWidth: 0 }}>
              <h5 style={{ margin: 0, fontWeight: 600, fontSize: '17px', color: '#1a1a1a' }}>
                {row.brand_name || brandInfo?.brand_name || '--'}
              </h5>
              <div className="d-flex flex-wrap align-items-center gap-3" style={{ marginTop: '6px', fontSize: '13px' }}>
                <span>
                  <span style={{ color: '#888', marginRight: '6px', fontSize: '12px' }}>Profile</span>
                  {profileBrandId ? (
                    <a
                      href={`/profile/${profileBrandId}/brand`}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: '#007bff', textDecoration: 'none' }}
                    >
                      Visit Profile
                    </a>
                  ) : (
                    <span style={{ color: '#999' }}>--</span>
                  )}
                </span>
                <span>
                  <span style={{ color: '#888', marginRight: '6px', fontSize: '12px' }}>Website</span>
                  {displayWebsite ? (
                    <a
                      href={displayWebsite.startsWith('http') ? displayWebsite : `https://${displayWebsite}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: '#007bff', textDecoration: 'none' }}
                    >
                      Visit Website
                    </a>
                  ) : (
                    <span style={{ color: '#999' }}>--</span>
                  )}
                </span>
              </div>
            </div>
          </div>

          <div className="d-flex align-items-center gap-3" style={{ flexShrink: 0 }}>
            <span style={statusBadgeStyle}>{row.status || '--'}</span>

            {row.request_type === 'client' && row.approved === false && (
              <Button
                color="success"
                size="sm"
                onClick={() => handleApproveBrandRequest(row.id)}
                disabled={approveLoadingId === row.id}
                className="d-flex align-items-center gap-1"
                style={{ fontSize: '12px', padding: '4px 12px' }}
              >
                {approveLoadingId === row.id ? 'Approving...' : 'Approve'}
              </Button>
            )}

            {row.request_type === 'client' && row.approved === true && (
              <span style={{ fontSize: '12px', color: '#28a745', fontWeight: '500', padding: '4px 12px', backgroundColor: '#d4edda', borderRadius: '4px', border: '1px solid #c3e6cb' }}>
                ✓ Approved
              </span>
            )}

            <Button
              color="secondary"
              size="sm"
              onClick={() => handleOpenAlertJan(row)}
              style={{ fontSize: '12px', padding: '4px 12px' }}
            >
              Alert Jan
            </Button>

            <button
              type="button"
              className="btn btn-outline-secondary btn-sm d-flex align-items-center gap-1"
              onClick={() => handleExportSingle(row.id)}
              disabled={isSingleExporting}
              style={{ fontSize: '12px', padding: '4px 10px' }}
            >
              <Download size={14} />
              {isSingleExporting ? 'Exporting...' : 'Export'}
            </button>

            {renderActionButtons(row)}
          </div>
        </div>

        {/* Meta Row */}
        <div style={metaRowStyle}>
          <div style={metaItemStyle}>
            <span style={metaLabelStyle}>Source</span>
            <span style={metaValueStyle}>{row.request_type || 'admin'}</span>
          </div>
          <div style={metaDivider} />
          <div style={metaItemStyle}>
            <span style={metaLabelStyle}>Match Type</span>
            <span style={metaValueStyle}>{row.match_type || '--'}</span>
          </div>
          <div style={metaDivider} />
          <div style={metaItemStyle}>
            <span style={metaLabelStyle}>Created</span>
            <span style={metaValueStyle}>{formatDetailDate(row.created_at)}</span>
          </div>
          <div style={metaDivider} />
          <div style={metaItemStyle}>
            <span style={metaLabelStyle}>Updated</span>
            <span style={metaValueStyle}>{formatDetailDate(row.updated_at)}</span>
          </div>
          <div style={metaDivider} />
          <div style={metaItemStyle}>
            <span style={metaLabelStyle}>Deadline</span>
            <span style={metaValueStyle}>{formatDetailDate(brandInfo?.deadline)}</span>
          </div>
          <div style={metaDivider} />
          <div style={metaItemStyle}>
            <span style={metaLabelStyle}>Approved By</span>
            <span style={metaValueStyle}>{row.approved_by_admin || '--'}</span>
          </div>
        </div>

        {/* Markets */}
        {markets.length > 0 && (
          <div style={sectionStyle}>
            <span style={sectionLabelStyle}>Markets of Interest</span>
            <div className="d-flex flex-wrap gap-2" style={{ marginTop: '6px' }}>
              {markets.map((market, index) => (
                <span key={`${row.id}-market-${index}`} style={marketTagStyle}>
                  {typeof market === 'string' ? market : market?.label}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Detail Sections Grid */}
        <div style={sectionsGridStyle}>
          <div style={sectionBoxStyle}>
            <div style={sectionBoxTitleStyle}>Brand In Your Space Setup</div>
            {brandInSpace.length ? (
              <div style={miniTableStyle}>
                <div style={miniTableHeaderStyle}>
                  <span style={{ flex: 1 }}>Brand Name</span>
                  <span style={{ flex: 1 }}>Website</span>
                </div>
                {brandInSpace.map((brand, idx) => (
                  <div key={`${row.id}-space-${idx}`} style={miniTableRowStyle}>
                    <span style={{ flex: 1 }}>{brand?.brand_name || '--'}</span>
                    <span style={{ flex: 1 }}>
                      {brand?.website ? (
                        <a href={getAbsoluteUrl(brand.website)} target="_blank" rel="noopener noreferrer" style={{ color: '#007bff', textDecoration: 'none' }}>
                          {brand.website}
                        </a>
                      ) : '--'}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <span style={emptyTextStyle}>No brands added</span>
            )}
          </div>

          <div style={sectionBoxStyle}>
            <div style={sectionBoxTitleStyle}>Exclusions</div>
            {exclusions.length ? (
              <div style={miniTableStyle}>
                <div style={miniTableHeaderStyle}>
                  <span style={{ flex: 1 }}>Brand Name</span>
                  <span style={{ flex: 1 }}>Website</span>
                </div>
                {exclusions.map((brand, idx) => (
                  <div key={`${row.id}-exclusion-${idx}`} style={miniTableRowStyle}>
                    <span style={{ flex: 1 }}>{brand?.name || '--'}</span>
                    <span style={{ flex: 1 }}>
                      {brand?.website ? (
                        <a href={getAbsoluteUrl(brand.website)} target="_blank" rel="noopener noreferrer" style={{ color: '#007bff', textDecoration: 'none' }}>
                          {brand.website}
                        </a>
                      ) : '--'}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <span style={emptyTextStyle}>No exclusions</span>
            )}
          </div>
        </div>

        <div style={sectionsGridStyle}>
          <div style={sectionBoxStyle}>
            <div style={sectionBoxTitleStyle}>Preferred Retailer</div>
            <div style={{ fontSize: '13px', color: '#444', lineHeight: '1.6' }}>
              {detailData?.preferred_retailer || '--'}
            </div>
          </div>

          <div style={sectionBoxStyle}>
            <div style={sectionBoxTitleStyle}>Matching Criteria</div>
            {findMatches?.type === 'standard' && (
              <div style={{ fontSize: '13px', color: '#444' }}>Standard Method</div>
            )}

            {findMatches?.type === 'unique' && findMatches?.unique_option === 'put_brand_retailers' && (
              <div style={{ fontSize: '13px', color: '#444' }}>
                <div style={{ fontWeight: 600, marginBottom: '4px' }}>Unique Method (Put Brand B Retailers in Brand A)</div>
                {brandA.map((brand, idx) => (
                  <div key={`${row.id}-brand-a-${idx}`}>
                    Target: <strong>{brand?.name || '--'}</strong> ({brand?.website || '--'})
                  </div>
                ))}
                {brandB.map((brand, idx) => (
                  <div key={`${row.id}-brand-b-${idx}`}>
                    Source: <strong>{brand?.name || '--'}</strong> ({brand?.website || '--'})
                  </div>
                ))}
                <div style={{ marginTop: '4px' }}>Type: {findMatches?.match_type || 'Copy'}</div>
              </div>
            )}

            {findMatches?.type === 'unique' && findMatches?.unique_option === 'take_working_retailers' && (
              <div style={{ fontSize: '13px', color: '#444' }}>
                <div style={{ fontWeight: 600, marginBottom: '4px' }}>Unique Method (Provide Brand in Your Space)</div>
                {brandsInSpaceForMatch.map((brand, idx) => (
                  <div key={`${row.id}-match-space-${idx}`}>
                    {brand?.brand_name || '--'} ({brand?.website || '--'})
                  </div>
                ))}
                <div style={{ marginTop: '4px' }}>Type: {findMatches?.match_type || 'Copy'}</div>
              </div>
            )}

            {!findMatches?.type && (
              <span style={emptyTextStyle}>--</span>
            )}
          </div>
        </div>
      </div>
    );
  };

  const selectedCount = selectAll ? totalBrandRequests : selectedBrandRequestIds.length;

  return (
    <div className="page-body">
      <Container fluid style={{ padding: "20px 0px" }}>
        <Row className="align-items-center justify-content-between mb-3">
          <Col xs="auto">
            <h4 className="card-title mb-0" style={{ fontWeight: '600', fontSize: '20px' }}>
              Brand Requests
            </h4>
            <h6 className="card-title mt-2" style={{ fontWeight: '400', fontSize: '16px', color: '#999' }}>
              Track all the Brand Requests details and status here
            </h6>
          </Col>
          <Col xs="auto" className="d-flex flex-row gap-3 align-items-center">
            <Button color="primary" onClick={() => navigate('/brand-requests/create')}>Add Brand Request</Button>
          </Col>
        </Row>
        <Card>
          <CardBody>
            <Row className="align-items-center justify-content-between mb-3">
              <Col xs="auto">
                <Input
                  type="text"
                  placeholder="Search request, brand name, url..."
                  value={searchTerm}
                  onChange={handleSearchChange}
                  style={{ minWidth: '500px' }}
                />
              </Col>
              <Col xs="auto" className="d-flex flex-row gap-2 align-items-center">
                <UncontrolledDropdown>
                  <DropdownToggle caret color="outline-primary" size="sm" style={{ minWidth: '140px' }}>
                    {statusFilter}
                  </DropdownToggle>
                  <DropdownMenu>
                    <DropdownItem onClick={() => handleStatusFilterChange('All Status')}>
                      All Status
                    </DropdownItem>
                    <DropdownItem onClick={() => handleStatusFilterChange('Matches Pending')}>
                      Matches Pending
                    </DropdownItem>
                    <DropdownItem onClick={() => handleStatusFilterChange('Matches Processing')}>
                      Matches Processing
                    </DropdownItem>
                  </DropdownMenu>
                </UncontrolledDropdown>

                <UncontrolledDropdown>
                  <DropdownToggle caret color="outline-primary" size="sm" style={{ minWidth: '150px' }}>
                    {matchTypeFilter}
                  </DropdownToggle>
                  <DropdownMenu>
                    <DropdownItem onClick={() => handleMatchTypeFilterChange('All Match Types')}>
                      All Match Types
                    </DropdownItem>
                    <DropdownItem onClick={() => handleMatchTypeFilterChange('Unique')}>
                      Unique
                    </DropdownItem>
                    <DropdownItem onClick={() => handleMatchTypeFilterChange('Standard')}>
                      Standard
                    </DropdownItem>
                  </DropdownMenu>
                </UncontrolledDropdown>

                <Input
                  type="date"
                  value={dateFilter}
                  onChange={handleDateFilterChange}
                  style={{ minWidth: '150px' }}
                />
              </Col>
            </Row>

            {/* View Tabs + Bulk Actions */}
            <div style={tabBarStyle}>
              <div className="d-flex align-items-center gap-1" style={{ background: '#f1f3f5', borderRadius: '8px', padding: '3px' }}>
                <button
                  type="button"
                  onClick={() => setActiveView('table')}
                  style={{
                    ...tabBtnStyle,
                    ...(activeView === 'table' ? tabBtnActiveStyle : {}),
                  }}
                >
                  <List size={15} />
                  Table View
                </button>
                <button
                  type="button"
                  onClick={() => setActiveView('detailed')}
                  style={{
                    ...tabBtnStyle,
                    ...(activeView === 'detailed' ? tabBtnActiveStyle : {}),
                  }}
                >
                  <Grid size={15} />
                  Detailed View
                </button>
              </div>

              {activeView === 'detailed' && (
                <div className="d-flex align-items-center gap-3">
                  <label className="d-flex align-items-center gap-2 mb-0" style={{ cursor: 'pointer', fontSize: '13px', color: '#555' }}>
                    <Input
                      type="checkbox"
                      checked={selectAll || allPageSelected}
                      onChange={(e) => handleSelectAllDetailed(e.target.checked)}
                      style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                    />
                    Select All ({totalBrandRequests})
                  </label>

                  {selectedCount > 0 && (
                    <span style={{ fontSize: '13px', color: '#007bff', fontWeight: 500 }}>
                      {selectedCount} selected
                    </span>
                  )}

                  <Button
                    color="success"
                    size="sm"
                    onClick={handleExportSelected}
                    disabled={exportLoading || selectedCount === 0}
                    className="d-flex align-items-center gap-1"
                  >
                    <Download size={14} />
                    {exportLoading ? 'Exporting...' : `Export CSV`}
                  </Button>
                </div>
              )}
            </div>

            {activeView === 'table' ? (
              <div className="table-responsive mt-3">
                <DataTable
                  striped
                  pagination
                  data={tableData}
                  columns={columns}
                  className="display"
                  paginationServer
                  progressPending={isLoading}
                  paginationTotalRows={totalBrandRequests}
                  paginationPerPage={itemsPerPage}
                  paginationDefaultPage={page}
                  customStyles={{ tableWrapper: { style: { minHeight: 400 } } }}
                  onChangePage={(newPage) => setPage(newPage)}
                  onChangeRowsPerPage={handleItemPerPageSelect}
                  onRowClicked={handleRowClick}
                  pointerOnHover
                  highlightOnHover
                  noDataComponent={
                    <div className="p-4 text-center text-muted">
                      No brand requests found
                    </div>
                  }
                />
              </div>
            ) : (
              <>
                <div style={{ marginTop: '16px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
                  {isLoading ? (
                    <div className="p-5 text-center text-muted">Loading...</div>
                  ) : tableData.length === 0 ? (
                    <div className="p-5 text-center text-muted">No brand requests found</div>
                  ) : (
                    tableData.map((row) => renderDetailedCard(row))
                  )}
                </div>

                {/* Pagination */}
                {totalBrandRequests > 0 && (
                  <div style={paginationBarStyle}>
                    <div className="d-flex align-items-center gap-2">
                      <span style={{ fontSize: '13px', color: '#666' }}>Rows per page:</span>
                      <select
                        value={itemsPerPage}
                        onChange={(e) => handleItemPerPageSelect(Number(e.target.value))}
                        style={paginationSelectStyle}
                      >
                        {[10, 15, 20, 25, 50].map((val) => (
                          <option key={val} value={val}>{val}</option>
                        ))}
                      </select>
                    </div>

                    <div className="d-flex align-items-center gap-3">
                      <span style={{ fontSize: '13px', color: '#666' }}>
                        {((page - 1) * itemsPerPage) + 1}
                        {' - '}
                        {Math.min(page * itemsPerPage, totalBrandRequests)}
                        {' of '}
                        {totalBrandRequests}
                      </span>
                      <div className="d-flex gap-1">
                        <button
                          type="button"
                          onClick={() => setPage((p) => Math.max(1, p - 1))}
                          disabled={page <= 1}
                          style={paginationBtnStyle}
                        >
                          <ChevronLeft size={18} />
                        </button>
                        <button
                          type="button"
                          onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                          disabled={page >= totalPages}
                          style={paginationBtnStyle}
                        >
                          <ChevronRight size={18} />
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
          </CardBody>
        </Card>
      </Container>

      <SendInvitationModal
        isOpen={invitationModalOpen}
        toggle={() => toggleInvitationModal(null)}
        brandRequest={selectedBrandRequest}
        onInvitationSent={handleInvitationSent}
      />

      <ConfirmationModal
        open={confirmationModalOpen}
        onClose={() => {
          setConfirmationModalOpen(false);
          setSingleDeleteBrandRequest(null);
        }}
        isLoading={deleteLoading}
        onSubmit={handleDelete}
        title="Delete Brand Request"
        message="Are you sure you want to permanently delete this brand request?"
      />

      <AlertJanModal
        isOpen={alertJanModalOpen}
        toggle={() => setAlertJanModalOpen(false)}
        brandRequest={alertJanBrandRequest}
        getBrandRequests={_getTableData}
      />

      {/* History Logs Drawer */}
      {historyDrawerOpen && (
        <>
          <div
            style={drawerOverlayStyle}
            onClick={closeHistoryDrawer}
          />
          <div style={drawerStyle}>
            <div style={drawerHeaderStyle}>
              <div>
                <h5 style={{ margin: 0, fontWeight: 600, fontSize: '18px' }}>History Logs</h5>
                {selectedHistoryRow && (
                  <>
                    <p style={{ margin: '4px 0 0 0', fontSize: '13px', color: '#666' }}>
                      {selectedHistoryRow.brand_name || '--'}
                    </p>
                    <p style={{ margin: '6px 0 0 0', fontSize: '13px', color: '#007bff', fontWeight: 500 }}>
                      Accumulative time: {formatElapsedTime(getTotalAccumulativeTime(selectedHistoryRow))}
                    </p>
                  </>
                )}
              </div>
              <button
                type="button"
                onClick={closeHistoryDrawer}
                style={closeButtonStyle}
              >
                ×
              </button>
            </div>
            <div style={drawerBodyStyle}>
              {selectedHistoryRow && (
                <div style={timelineContainerStyle}>
                  {(() => {
                    const timelineEvents = [...getHistoryTimelineEvents(selectedHistoryRow)].reverse();
                    const startTime = selectedHistoryRow?.created_at;

                    return timelineEvents.map((event, index) => {
                      const isLast = index === timelineEvents.length - 1;
                      const accumulativeMs = getElapsedMs(startTime, event.created_at);

                      return (
                        <div
                          key={`${event.action}-${index}`}
                          style={{
                            ...timelineItemStyle,
                            borderLeft: isLast ? 'none' : '2px solid #e0e0e0',
                          }}
                        >
                          <div style={timelineDotStyle} />
                          <div style={timelineContentStyle}>
                            <div style={timelineActionStyle}>{event.action}</div>
                            <div style={timelineDetailStyle}>
                              <span style={timelineLabelStyle}>By</span>
                              <span style={timelineValueStyle}>{event.action_by || '--'}</span>
                            </div>
                            <div style={timelineDetailStyle}>
                              <span style={timelineLabelStyle}>Time</span>
                              <span style={timelineValueStyle}>
                                {formatDetailDate(event.created_at)}
                              </span>
                            </div>
                            <div style={timelineDetailStyle}>
                              <span style={timelineLabelStyle}>Elapsed</span>
                              <span style={timelineValueStyle}>{formatElapsedTime(accumulativeMs)}</span>
                            </div>
                          </div>
                        </div>
                      );
                    });
                  })()}
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  )
};

/* ── Styles ── */

const statusBadgeStyle = {
  padding: '4px 12px',
  borderRadius: '24px',
  fontSize: '12px',
  fontWeight: '500',
  backgroundColor: '#F7F7F7',
  textAlign: 'center',
  color: '#212121',
  border: '1px solid #D9D9D9',
  whiteSpace: 'nowrap',
};

const tabBarStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: '12px',
};

const tabBtnStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
  padding: '7px 16px',
  border: 'none',
  borderRadius: '6px',
  fontSize: '13px',
  fontWeight: 500,
  cursor: 'pointer',
  background: 'transparent',
  color: '#666',
  transition: 'all 0.15s',
};

const tabBtnActiveStyle = {
  background: '#fff',
  color: '#111',
  boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
};

const detailedCardStyle = {
  background: '#fff',
  border: '1px solid #e9ecef',
  borderRadius: '12px',
  padding: '0',
  overflow: 'hidden',
  transition: 'border-color 0.15s, box-shadow 0.15s',
};

const cardHeaderStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '16px 20px',
  borderBottom: '1px solid #f0f0f0',
  flexWrap: 'wrap',
  gap: '12px',
};

const metaRowStyle = {
  display: 'flex',
  alignItems: 'center',
  padding: '12px 20px',
  background: '#fafbfc',
  borderBottom: '1px solid #f0f0f0',
  flexWrap: 'wrap',
  gap: '8px',
};

const metaItemStyle = {
  display: 'flex',
  flexDirection: 'column',
  gap: '2px',
  minWidth: '100px',
};

const metaLabelStyle = {
  fontSize: '11px',
  fontWeight: 600,
  color: '#999',
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
};

const metaValueStyle = {
  fontSize: '13px',
  color: '#333',
  fontWeight: 500,
};

const metaDivider = {
  width: '1px',
  height: '28px',
  background: '#e0e0e0',
  margin: '0 8px',
};

const sectionStyle = {
  padding: '12px 20px',
  borderBottom: '1px solid #f0f0f0',
};

const sectionLabelStyle = {
  fontSize: '12px',
  fontWeight: 600,
  color: '#888',
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
};

const marketTagStyle = {
  padding: '3px 10px',
  borderRadius: '14px',
  fontSize: '12px',
  fontWeight: '500',
  backgroundColor: '#e8f4fd',
  color: '#0066cc',
  border: '1px solid #b3d9f2',
};

const sectionsGridStyle = {
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: '0',
};

const sectionBoxStyle = {
  padding: '14px 20px',
  borderBottom: '1px solid #f0f0f0',
  borderRight: '1px solid #f0f0f0',
};

const sectionBoxTitleStyle = {
  fontSize: '13px',
  fontWeight: 600,
  color: '#333',
  marginBottom: '8px',
};

const miniTableStyle = {
  border: '1px solid #eee',
  borderRadius: '6px',
  overflow: 'hidden',
  fontSize: '12px',
};

const miniTableHeaderStyle = {
  display: 'flex',
  padding: '6px 10px',
  background: '#f8f9fa',
  fontWeight: 600,
  color: '#666',
  borderBottom: '1px solid #eee',
};

const miniTableRowStyle = {
  display: 'flex',
  padding: '6px 10px',
  borderBottom: '1px solid #f5f5f5',
  color: '#444',
};

const emptyTextStyle = {
  fontSize: '13px',
  color: '#bbb',
  fontStyle: 'italic',
};

const paginationBarStyle = {
  display: 'flex',
  justifyContent: 'flex-end',
  alignItems: 'center',
  gap: '24px',
  padding: '16px 0 0 0',
  borderTop: '1px solid #eee',
  marginTop: '16px',
};

const paginationSelectStyle = {
  padding: '4px 8px',
  borderRadius: '4px',
  border: '1px solid #ddd',
  fontSize: '13px',
  background: '#fff',
  cursor: 'pointer',
};

const paginationBtnStyle = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '32px',
  height: '32px',
  border: '1px solid #ddd',
  borderRadius: '6px',
  background: '#fff',
  cursor: 'pointer',
  color: '#333',
};

const drawerOverlayStyle = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(0, 0, 0, 0.5)',
  zIndex: 1040,
  animation: 'fadeIn 0.3s ease',
};

const drawerStyle = {
  position: 'fixed',
  top: 0,
  right: 0,
  bottom: 0,
  width: '450px',
  maxWidth: '90vw',
  backgroundColor: '#fff',
  boxShadow: '-2px 0 8px rgba(0, 0, 0, 0.15)',
  zIndex: 1050,
  display: 'flex',
  flexDirection: 'column',
  animation: 'slideInRight 0.3s ease',
};

const drawerHeaderStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '20px 24px',
  borderBottom: '1px solid #e9ecef',
  backgroundColor: '#fff',
};

const closeButtonStyle = {
  background: 'none',
  border: 'none',
  fontSize: '32px',
  lineHeight: '1',
  cursor: 'pointer',
  color: '#666',
  padding: '0',
  width: '32px',
  height: '32px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

const drawerBodyStyle = {
  flex: 1,
  overflowY: 'auto',
  padding: '24px',
};

const timelineContainerStyle = {
  position: 'relative',
};

const timelineItemStyle = {
  position: 'relative',
  paddingLeft: '32px',
  paddingBottom: '24px',
  borderLeft: '2px solid #e0e0e0',
  marginLeft: '5px',
};

const timelineDotStyle = {
  position: 'absolute',
  left: '-6px',
  top: '4px',
  width: '12px',
  height: '12px',
  borderRadius: '50%',
  backgroundColor: '#007bff',
  border: '2px solid #fff',
  boxShadow: '0 0 0 2px #007bff',
  zIndex: 1,
};

const timelineContentStyle = {
  display: 'flex',
  flexDirection: 'column',
  gap: '6px',
  position: 'relative',
  paddingLeft: '8px',
};

const timelineActionStyle = {
  fontSize: '15px',
  fontWeight: 600,
  color: '#1a1a1a',
  marginBottom: '4px',
};

const timelineDetailStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  fontSize: '13px',
};

const timelineLabelStyle = {
  color: '#666',
  fontWeight: 500,
  minWidth: '45px',
};

const timelineValueStyle = {
  color: '#333',
  fontWeight: 400,
};

const mapStateToProps = (state) => ({
  user: state.auth.user,
  brandRequests: state.brandRequests.brandRequests || {},
  totalBrandRequests: state.brandRequests.totalBrandRequests || 0,
  isLoading: state.brandRequests.isLoading,
});

const mapDispatchToProps = {
  getBrandRequests: getBrandRequestsAction,
  deleteBrandRequest: deleteBrandRequestAction,
  approveBrandRequest: approveBrandRequestAction,
  adminUpdateBrandRequest: adminUpdateBrandRequestAction,
};

export default connect(mapStateToProps, mapDispatchToProps)(BrandRequests);